import { Check, Done, Edit } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Divider,
    FormControl,
    FormLabel,
    IconButton,
    MenuItem,
    Paper,
    TextField,
    Typography,
} from '@mui/material';
import type { ICompanyLocalisation } from '@sharedInterfaces/ICompanySettings';
import { type ELanguage, getLanguageOfCountry, getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import type { ICountriesTexts } from '@sharedInterfaces/Language/languageTexts/countries';
import type { IIndustriesTexts } from '@sharedInterfaces/Language/languageTexts/industriesTexts';
import { subNationLabels } from '@sharedInterfaces/Language/languageTexts/subNationLabel';
import changeCompanyName from '@src/APIs/graphQl/Company/changeCompanyName';
import updateGeneralCompanySettings from '@src/APIs/graphQl/Company/updateGeneralCompanySettings';
import CountrySelect from '@src/App/Components/General/FormularElements/formsControls/inputs/CountrySelect/CountrySelect';
import IndustrySelect from '@src/App/Components/General/FormularElements/formsControls/inputs/IndustrySelect/IndustrySelect';
import LanguageSelect from '@src/App/Components/General/FormularElements/formsControls/inputs/LanguageSelect/LanguageSelect';
import SubCountrySelect from '@src/App/Components/General/FormularElements/formsControls/inputs/SubCountrySelect/SubCountrySelect';
import type Company from '@src/Objects/Company';
import { setCompanyName } from '@store/reducer/companyReducer';
import type { AppState } from '@store/store';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

interface GeneralTabProps {
    company: Company;
    setCompany: (company: Company) => void;
}
/**
 * GeneralTab component.
 *
 * @param {Object} company - The company object.
 * @param {Function} setCompany - The function to set the company object.
 * @returns {JSX.Element} The rendered GeneralTab component.
 */
export function GeneralTab({ company, setCompany }: GeneralTabProps) {
    const dispatch = useDispatch();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).settings;
    const [localisation, setLocalisation] = useState<ICompanyLocalisation>({
        language: '' as ELanguage,
        country: '' as keyof ICountriesTexts,
        subCountry: '',
    });
    const [industry, setIndustry] = useState<keyof IIndustriesTexts>('' as keyof IIndustriesTexts);
    const [teamSize, setTeamSize] = useState<string>('');
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saving, setSaving] = useState<boolean>(false);
    const [changed, setChanged] = useState<boolean>(false);
    const [editName, setEditName] = useState<string>('');
    const [savingName, setSavingName] = useState<boolean>(false);

    useEffect(() => {
        if (company.localisation) {
            setLocalisation({ ...company.localisation });
        }
        if (company.industry) {
            setIndustry(company.industry);
        }
        // TODO: Backend - Initialize teamSize from company.teamSize when field exists
        // if (company.teamSize) {
        //     setTeamSize(company.teamSize);
        // }
    }, [company]);
    useEffect(() => {
        if (localisation && localisation.country && !localisation.language) {
            setLocalisation({ ...localisation, language: getLanguageOfCountry(localisation.country) });
        }
    }, [localisation.country]);

    return (
        <Paper sx={{ mt: 3, p: 3, bgcolor: 'background.paper' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    {langStrings.general}
                </Typography>
                <Divider />

                <FormControl>
                    <FormLabel>{langStrings.title}</FormLabel>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {editName === '' ? (
                            <>
                                <Typography variant="h6">{company.name}</Typography>
                                <IconButton
                                    size="small"
                                    onClick={() => setEditName(company.name)}
                                    disabled={savingName}
                                >
                                    <Edit />
                                </IconButton>
                            </>
                        ) : (
                            <>
                                <TextField
                                    value={editName}
                                    onChange={(e) => setEditName(e.target.value)}
                                    variant="outlined"
                                    size="small"
                                    sx={{ flexGrow: 1 }}
                                />
                                <IconButton
                                    size="small"
                                    onClick={() => {
                                        if (company.name !== editName) {
                                            setSavingName(true);
                                            changeCompanyName(editName)
                                                .then((result) => {
                                                    setCompany(result);
                                                    dispatch(setCompanyName(result.name));
                                                    setEditName('');
                                                    setSavingName(false);
                                                })
                                                .catch(() => {
                                                    alert(langStrings.errorChangeName);
                                                    setSavingName(false);
                                                });
                                        } else {
                                            setEditName('');
                                        }
                                    }}
                                    disabled={savingName}
                                >
                                    <Check />
                                </IconButton>
                            </>
                        )}
                    </Box>
                </FormControl>
                <FormControl>
                    <FormLabel>{langStrings.country}</FormLabel>
                    <CountrySelect
                        country={localisation.country}
                        setCountry={(value) => {
                            setLocalisation({ ...localisation, country: value });
                            setChanged(true);
                        }}
                    />
                </FormControl>

                {localisation.country && (
                    <>
                        <FormControl>
                            <FormLabel>{langStrings.language}</FormLabel>
                            <LanguageSelect
                                language={localisation.language}
                                setLanguage={(value) => {
                                    setLocalisation({ ...localisation, language: value });
                                    setChanged(true);
                                }}
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel>{subNationLabels[lang][localisation.country]}</FormLabel>
                            <SubCountrySelect
                                country={localisation.country}
                                subCountry={localisation.subCountry}
                                setInnerCountry={(value) => {
                                    setLocalisation({ ...localisation, subCountry: value });
                                    setChanged(true);
                                }}
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel>{langStrings.defaultLocation}</FormLabel>
                            <TextField
                                value={/* TODO: Backend - Add company.defaultLocation field */ ''}
                                onChange={(e) => {
                                    // TODO: Backend - Implement company.defaultLocation save functionality
                                    setChanged(true);
                                }}
                                placeholder="z.B. Berlin, München, Hamburg"
                                variant="outlined"
                                size="small"
                                fullWidth
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel>{langStrings.industry}</FormLabel>
                            <IndustrySelect
                                industry={industry}
                                setIndustry={(value) => {
                                    setIndustry(value);
                                    setChanged(true);
                                }}
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel>Firmengröße</FormLabel>
                            <TextField
                                select
                                value={teamSize}
                                onChange={(e) => {
                                    setTeamSize(e.target.value);
                                    // TODO: Backend - Implement company.teamSize save functionality
                                    setChanged(true);
                                }}
                                variant="outlined"
                                size="small"
                                fullWidth
                            >
                                <MenuItem value="1-10">1-10 Personen</MenuItem>
                                <MenuItem value="11-50">11-50 Personen</MenuItem>
                                <MenuItem value="51-200">51-200 Personen</MenuItem>
                                <MenuItem value="201-1000">201-1000 Personen</MenuItem>
                                <MenuItem value="1000+">1000+ Personen</MenuItem>
                            </TextField>
                        </FormControl>
                    </>
                )}

                {saveError && (
                    <Alert severity="error" onClose={() => setSaveError(null)}>
                        {saveError}
                    </Alert>
                )}

                {changed && (
                    <Button
                        variant="contained"
                        startIcon={<Done />}
                        disabled={
                            saving ||
                            !localisation.country ||
                            !localisation.language ||
                            !localisation.subCountry ||
                            !industry
                        }
                        onClick={() => {
                            setSaving(true);
                            updateGeneralCompanySettings(
                                localisation,
                                industry /* TODO: Backend - Add defaultLocation and teamSize parameters */,
                            )
                                .then(setCompany)
                                .then(setChanged.bind(null, false))
                                .catch((err) => setSaveError(err.toString()))
                                .finally(() => setSaving(false));
                        }}
                    >
                        {langStrings.save}
                    </Button>
                )}
            </Box>
        </Paper>
    );
}
